import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import {
  formatNumber,
  getCarBrandLogo,
  getCarImages,
  getColorImgSrc,
} from '@/core';
import { Card, Text } from '@/ui';

import { Chip } from '../chip';
import { Battery } from '../icons/battery';
import { Seat } from '../icons/seat';
import { SteeringWheel } from '../icons/steering-wheel';

interface MapCarCardProps {
  car: Car;
  onPress: () => void;
}

export default function MapCarCard({ car, onPress }: MapCarCardProps) {
  const { styles, theme } = useStyles(stylesheet);

  const colorImgSrc = useMemo(() => {
    return getColorImgSrc(car.color);
  }, [car.color]);

  return (
    <Card style={styles.container}>
      <Pressable style={{ flexGrow: 1 }} onPress={onPress}>
        <View
          style={{
            flexGrow: 1,
            padding: 8,
            gap: 16,
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.icon.fill,
                  padding: 10,
                  borderRadius: 8,
                  width: 40,
                  height: 40,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Image
                  style={{
                    width: 26,
                    height: 26,
                  }}
                  source={getCarBrandLogo(car.brandLogos)}
                  contentFit="cover"
                />
              </View>

              <View>
                <Text type="bodyBold">{`${car.brandName} ${car.modelName}`}</Text>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {car.modelType}
                </Text>
              </View>
            </View>

            <View style={{ flex: 1, alignItems: 'flex-end' }}>
              <Image
                source={{
                  uri:
                    car && getCarImages(car.images).length > 0
                      ? getCarImages(car.images, 'small')[0].url
                      : '',
                }}
                style={{ width: '100%', maxWidth: 180, aspectRatio: 3 }}
                contentFit="cover"
              />
            </View>
          </View>

          <View
            style={{
              gap: 8,
              flexGrow: 1,
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                width: '75%',
                gap: 4,
                alignSelf: 'flex-end',
              }}
            >
              <Chip icon={<SteeringWheel />} text={car.autopilotType} />

              <Chip icon={<Battery />} text={`${car.batteryRange} Miles`} />

              <Chip icon={<Seat />} text={`${car.numSeats} Seats`} />

              <Chip
                imgSrc={colorImgSrc}
                useDefaultIconFillColor={false}
                text="Exterior"
              />
            </View>

            <View style={{ alignSelf: 'flex-end' }}>
              <View style={{ flexDirection: 'row', gap: 4 }}>
                {car.discountedDailyRate ? (
                  <Text
                    type="bodyRegular"
                    style={{ textDecorationLine: 'line-through' }}
                  >
                    {`$${car.dailyRate}`}
                  </Text>
                ) : null}

                <Text type="bodyBold">{`$${
                  car.discountedDailyRate || car.dailyRate
                } / day`}</Text>
              </View>

              <View style={{ alignSelf: 'flex-end' }}>
                <Text>{`Total $${formatNumber(car.totalRate)}`}</Text>
              </View>
            </View>
          </View>
        </View>
      </Pressable>
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    width: {
      md: 400,
    },
    height: 180,
    padding: 8,
    gap: 16,
  },
}));
